import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Modal, Form, Input, Switch, Upload, message, Alert, Divider, Tabs, Select, Table, Tag, Badge, Tooltip, Empty, Progress, Checkbox } from 'antd';
import { UploadOutlined, TableOutlined, CloudUploadOutlined, CheckCircleOutlined, CloseCircleOutlined, HistoryOutlined, ReloadOutlined, ClockCircleOutlined, ExclamationCircleOutlined, SyncOutlined, DownloadOutlined, SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import FirmwareList from '../../components/Firmware/FirmwareList';
import { safeFormatTimestamp } from '../../utils/timezone';
import websocketService from '../../services/websocket';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { TextArea } = Input;

const Firmware = () => {
  const [firmwares, setFirmwares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadForm] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceSelectionModalVisible, setDeviceSelectionModalVisible] = useState(false);
  const [selectedFirmwareId, setSelectedFirmwareId] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [deployProgress, setDeployProgress] = useState({});
  const [customers, setCustomers] = useState([]);
  const [deviceFilters, setDeviceFilters] = useState({ status: null, customer_id: null, search: '' });
  const [otaHistory, setOtaHistory] = useState([]);
  const [otaHistoryLoading, setOtaHistoryLoading] = useState(false);
  const [otaSearchText, setOtaSearchText] = useState('');
  const [deploymentReport, setDeploymentReport] = useState([]);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [otaProgress, setOtaProgress] = useState({});
  const [forceUpdate, setForceUpdate] = useState(false);

  useEffect(() => {
    fetchFirmwares();
    fetchDevices();
    fetchCustomers();
    fetchOTAHistory();
  }, []);

  // WebSocket OTA progress & status
  useEffect(() => {
    const handleOtaProgress = (data) => {
      if (data.type === 'ota_progress') {
        setOtaProgress(prev => ({
          ...prev,
          [data.device_id]: {
            percent: data.data.progress_percent,
            stage: data.data.stage,
            speed: data.data.download_speed_kbps,
            bytes: data.data.bytes_downloaded,
            total: data.data.total_bytes
          }
        }));
      }
    };

    const handleOtaStatus = (data) => {
      if (data.type === 'ota_status') {
        const statusData = data.data;
        if (statusData.status === 'completed' || statusData.status === 'failed') {
          setTimeout(() => {
            setOtaProgress(prev => {
              const next = { ...prev };
              delete next[data.device_id];
              return next;
            });
          }, 3000);
          fetchOTAHistory();
        }
      }
    };

    const unsubProgress = websocketService.subscribe('ota_progress', handleOtaProgress);
    const unsubStatus = websocketService.subscribe('ota_status', handleOtaStatus);
    return () => { unsubProgress(); unsubStatus(); };
  }, []);

  const fetchFirmwares = async () => {
    try {
      const response = await axios.get('/api/v1/firmware');
      setFirmwares(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Firmware listesi yüklenirken hata');
      setFirmwares([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      setDevicesLoading(true);
      const response = await axios.get('/api/v1/devices');
      setDevices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Devices fetch error:', error);
    } finally {
      setDevicesLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/v1/customers');
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Customers fetch error:', error);
    }
  };

  const fetchOTAHistory = async (searchQuery = '') => {
    try {
      setOtaHistoryLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (searchQuery) params.append('search', searchQuery);
      const response = await axios.get(`/api/v1/firmware/ota-history?${params}`);
      setOtaHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('OTA history fetch error:', error);
      setOtaHistory([]);
    } finally {
      setOtaHistoryLoading(false);
    }
  };

  const handleUpload = async (values) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', values.file.file);
      formData.append('version', values.version);
      formData.append('description', values.description || '');
      formData.append('release_notes', values.release_notes || '');
      formData.append('is_critical', values.is_critical || false);
      await axios.post('/api/v1/firmware/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      message.success('Firmware yüklendi');
      setUploadModalVisible(false);
      fetchFirmwares();
    } catch (error) {
      message.error('Yükleme hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/v1/firmware/${id}`);
      message.success('Firmware silindi');
      fetchFirmwares();
    } catch (error) {
      message.error('Silme hatası');
    }
  };

  const handleDeploy = (firmwareId) => {
    setSelectedFirmwareId(firmwareId);
    setSelectedDevices([]);
    setForceUpdate(false);
    setDeviceSelectionModalVisible(true);
  };

  const handleBulkDeploy = async () => {
    if (selectedDevices.length === 0) { message.warning('En az bir cihaz seçin'); return; }
    try {
      setDeploying(true);
      const results = await Promise.all(selectedDevices.map(async (device) => {
        try {
          setDeployProgress(prev => ({ ...prev, [device.id]: 0 }));
          const response = await axios.post(`/api/v1/firmware/${selectedFirmwareId}/deploy/${device.id}`, { force: forceUpdate });
          setDeployProgress(prev => ({ ...prev, [device.id]: 100 }));
          return { device_id: device.id, device_name: device.name, firmware_id: selectedFirmwareId, success: true, message: response.data.message };
        } catch (error) {
          setDeployProgress(prev => ({ ...prev, [device.id]: -1 }));
          return { device_id: device.id, device_name: device.name, firmware_id: selectedFirmwareId, success: false, error: error.response?.data?.detail || error.message };
        }
      }));
      setDeploymentReport(results);
      setReportModalVisible(true);
      setTimeout(() => setDeployProgress({}), 3000);
      setDeviceSelectionModalVisible(false);
      setSelectedDevices([]);
    } catch (error) {
      message.error('Toplu dağıtım hatası');
    } finally {
      setDeploying(false);
    }
  };

  const getFilteredDevices = () => {
    let filtered = devices;
    if (deviceFilters.status) filtered = filtered.filter(d => d.status === deviceFilters.status);
    if (deviceFilters.customer_id) filtered = filtered.filter(d => d.customer_id === deviceFilters.customer_id);
    if (deviceFilters.search) {
      const s = deviceFilters.search.toLowerCase();
      filtered = filtered.filter(d => d.name.toLowerCase().includes(s) || d.mac_address.toLowerCase().includes(s) || d.customer?.name?.toLowerCase().includes(s));
    }
    return filtered;
  };

  const otaStatusConfig = {
    pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Bekliyor' },
    downloading: { color: 'blue', text: 'İndiriliyor' },
    installing: { color: 'cyan', text: 'Yükleniyor' },
    completed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Tamamlandı' },
    failed: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Başarısız' },
    cancelled: { color: 'default', text: 'İptal' },
    timeout: { color: 'volcano', icon: <ClockCircleOutlined />, text: 'Zaman Aşımı' }
  };

  const tabItems = [
    {
      key: 'list',
      label: <Space><TableOutlined /><span>Firmware Listesi</span></Space>,
      children: (
        <FirmwareList
          firmwares={firmwares}
          loading={loading}
          onDeploy={handleDeploy}
          onDelete={handleDelete}
          onRefresh={() => { fetchFirmwares(); fetchDevices(); }}
        />
      ),
    },
    {
      key: 'ota-history',
      label: <Space><HistoryOutlined /><span>OTA Geçmişi</span><Badge count={otaHistory.length} /></Space>,
      children: (
        <Card
          title="Tüm OTA Dağıtım Geçmişi"
          extra={
            <Space>
              <Input.Search
                placeholder="Cihaz, MAC, versiyon ara..."
                allowClear
                value={otaSearchText}
                onChange={(e) => setOtaSearchText(e.target.value)}
                onSearch={(v) => fetchOTAHistory(v)}
                style={{ width: 300 }}
                enterButton
              />
              <Button icon={<ReloadOutlined />} onClick={() => fetchOTAHistory()} loading={otaHistoryLoading}>Yenile</Button>
            </Space>
          }
        >
          <Table
            dataSource={otaHistory}
            rowKey={(r) => r.id}
            loading={otaHistoryLoading}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Toplam ${t} kayıt` }}
            size="small"
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Henüz OTA yapılmamış" /> }}
            columns={[
              {
                title: 'Tarih', dataIndex: 'started_at', key: 'started_at', width: 170,
                render: (t) => t ? safeFormatTimestamp(t) : '-',
                sorter: (a, b) => new Date(a.started_at) - new Date(b.started_at)
              },
              {
                title: 'Cihaz', dataIndex: 'device_name', key: 'device_name', width: 170,
                render: (name, r) => (
                  <div>
                    <Text strong>{name || '-'}</Text>
                    <br /><Text type="secondary" style={{ fontSize: 11 }}>{r.device_mac}</Text>
                  </div>
                )
              },
              {
                title: 'Firmware', dataIndex: 'firmware_version', key: 'firmware_version', width: 110,
                render: (v) => v ? <Tag color="blue">{v}</Tag> : '-'
              },
              {
                title: 'Durum', dataIndex: 'status', key: 'status', width: 130,
                render: (status) => {
                  const c = otaStatusConfig[status] || { color: 'default', text: status };
                  return <Tag color={c.color} icon={c.icon}>{c.text}</Tag>;
                }
              },
              {
                title: 'Detay', key: 'detail', width: 220,
                render: (_, r) => {
                  // Pending/timeout: ilerleme durumu
                  if ((r.status === 'pending' || r.status === 'timeout') && r.last_stage) {
                    const stageMap = { downloading: 'İndiriliyor', verifying: 'Doğrulanıyor', installing: 'Yükleniyor' };
                    return (
                      <Tooltip title={r.last_activity_at ? `Son aktivite: ${safeFormatTimestamp(r.last_activity_at)}` : ''}>
                        <Space size={4}>
                          <SyncOutlined spin={r.status === 'pending'} />
                          <Text type="secondary">{stageMap[r.last_stage] || r.last_stage}</Text>
                          {r.last_progress_percent != null && <Text strong>{r.last_progress_percent}%</Text>}
                        </Space>
                      </Tooltip>
                    );
                  }
                  // Failed: hata mesajı
                  if (r.status === 'failed' && r.error_message) {
                    return <Text type="danger" style={{ fontSize: 11 }}>{r.error_message}</Text>;
                  }
                  // Completed: süre
                  if (r.status === 'completed' && r.duration_seconds) {
                    return <Text type="success" style={{ fontSize: 11 }}>{Math.floor(r.duration_seconds / 60)}dk {Math.floor(r.duration_seconds % 60)}sn</Text>;
                  }
                  return '-';
                }
              },
              {
                title: 'Bitiş', dataIndex: 'completed_at', key: 'completed_at', width: 150,
                render: (t) => t ? <Text style={{ fontSize: 11 }}>{safeFormatTimestamp(t)}</Text> : '-'
              }
            ]}
          />
        </Card>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>
          <CloudUploadOutlined /> Firmware Yönetimi
        </Title>
        <Space>
          <Button onClick={() => { fetchFirmwares(); fetchDevices(); }}>Yenile</Button>
          <Button type="primary" icon={<UploadOutlined />} onClick={() => { uploadForm.resetFields(); setUploadModalVisible(true); }}>Firmware Ekle</Button>
        </Space>
      </div>

      {/* Active OTA Progress */}
      {Object.keys(otaProgress).length > 0 && (
        <Card size="small" style={{ marginBottom: 16, border: '2px solid #1890ff', background: '#e6f7ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <SyncOutlined spin style={{ color: '#1890ff' }} />
            <Text strong style={{ color: '#1890ff' }}>Aktif OTA Güncellemeleri</Text>
            <Badge count={Object.keys(otaProgress).length} style={{ backgroundColor: '#52c41a' }} />
          </div>
          {Object.entries(otaProgress).map(([deviceId, progress]) => {
            const device = devices.find(d => d.id === parseInt(deviceId));
            const stageMap = { downloading: 'İndiriliyor', verifying: 'Doğrulanıyor', installing: 'Yükleniyor' };
            return (
              <div key={deviceId} style={{ background: 'white', padding: 8, borderRadius: 6, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 150 }}>
                  <Text strong>{device?.name || `#${deviceId}`}</Text>
                  <br /><Text type="secondary" style={{ fontSize: 11 }}>{stageMap[progress.stage] || progress.stage}</Text>
                </div>
                <div style={{ flex: 1 }}>
                  <Progress percent={progress.percent} size="small" status="active" strokeColor={{ '0%': '#1890ff', '100%': '#52c41a' }} />
                </div>
                {progress.speed && <Text type="secondary" style={{ fontSize: 11 }}>{progress.speed.toFixed(1)} KB/s</Text>}
              </div>
            );
          })}
        </Card>
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      {/* Upload Modal */}
      <Modal title="Firmware Yükle" open={uploadModalVisible} onCancel={() => setUploadModalVisible(false)} footer={null} width={700}>
        <Alert message="Firmware dosyası server'a yüklenecek ve OTA ile dağıtılacaktır." type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={uploadForm} layout="vertical" onFinish={handleUpload}>
          <Form.Item name="file" label="Firmware Dosyası" rules={[{ required: true, message: 'Dosya seçiniz' }]}>
            <Upload beforeUpload={() => false} accept=".bin,.hex,.elf" maxCount={1}>
              <Button icon={<UploadOutlined />}>Dosya Seç</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="version" label="Versiyon" rules={[{ required: true, message: 'Versiyon gerekli' }, { pattern: /^v?\d+\.\d+\.\d+(-[a-zA-Z0-9]+)?$/, message: 'Geçerli format: 1.0.0 veya v1.0.0-beta' }]}>
            <Input placeholder="Örn: 1.0.0" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama" rules={[{ max: 200 }]}>
            <Input placeholder="Kısa açıklama" />
          </Form.Item>
          <Form.Item name="release_notes" label="Release Notları" rules={[{ max: 2000 }]}>
            <TextArea rows={4} placeholder="Değişiklikler..." />
          </Form.Item>
          <Form.Item name="is_critical" label="Kritik Güncelleme" valuePropName="checked">
            <Switch checkedChildren="Kritik" unCheckedChildren="Normal" />
          </Form.Item>
          <Divider />
          <Space>
            <Button type="primary" htmlType="submit" icon={<UploadOutlined />} loading={uploading}>Yükle</Button>
            <Button onClick={() => setUploadModalVisible(false)}>İptal</Button>
          </Space>
        </Form>
      </Modal>

      {/* Device Selection Modal */}
      <Modal
        title={<Space><CloudUploadOutlined /><span>Firmware Dağıtımı - Cihaz Seçimi</span></Space>}
        open={deviceSelectionModalVisible}
        onCancel={() => { setDeviceSelectionModalVisible(false); setSelectedDevices([]); setForceUpdate(false); }}
        footer={[
          <Button key="cancel" onClick={() => { setDeviceSelectionModalVisible(false); setSelectedDevices([]); setForceUpdate(false); }}>İptal</Button>,
          <Button key="deploy" type="primary" icon={<CloudUploadOutlined />} loading={deploying} onClick={handleBulkDeploy} disabled={selectedDevices.length === 0}>
            {selectedDevices.length > 0 ? `${selectedDevices.length} Cihaza Dağıt` : 'Cihaz Seçin'}
          </Button>
        ]}
        width={1100}
      >
        <div style={{ marginBottom: 12 }}>
          <Checkbox checked={forceUpdate} onChange={(e) => setForceUpdate(e.target.checked)}>
            <Text strong>Zorla Güncelle</Text> <Text type="secondary" style={{ fontSize: 12 }}>(Aynı versiyon olsa bile)</Text>
          </Checkbox>
        </div>

        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search placeholder="Cihaz, MAC, müşteri ara..." value={deviceFilters.search} onChange={(e) => setDeviceFilters(prev => ({ ...prev, search: e.target.value }))} style={{ width: 260 }} allowClear />
          <Select placeholder="Durum" value={deviceFilters.status} onChange={(v) => setDeviceFilters(prev => ({ ...prev, status: v }))} style={{ width: 140 }} allowClear>
            <Select.Option value="online">Çevrimiçi</Select.Option>
            <Select.Option value="offline">Çevrimdışı</Select.Option>
            <Select.Option value="maintenance">Bakım</Select.Option>
          </Select>
          <Select placeholder="Müşteri" value={deviceFilters.customer_id} onChange={(v) => setDeviceFilters(prev => ({ ...prev, customer_id: v }))} style={{ width: 180 }} allowClear showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
            {customers.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
          </Select>
          <Button onClick={() => setDeviceFilters({ status: null, customer_id: null, search: '' })} size="small">Temizle</Button>
        </div>

        <Table
          dataSource={getFilteredDevices()}
          loading={devicesLoading}
          rowKey="id"
          size="small"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedDevices.map(d => d.id),
            onChange: (_, rows) => setSelectedDevices(rows),
            getCheckboxProps: (r) => ({ disabled: r.status !== 'online' }),
          }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} cihaz` }}
          scroll={{ x: 1000 }}
          columns={[
            { title: 'Cihaz', dataIndex: 'name', key: 'name', width: 160, ellipsis: true, render: (t, r) => (
              <div>
                <Text strong>{t}</Text>
                {r.model && <><br /><Tag color="blue" style={{ fontSize: 11 }}>{r.model}</Tag></>}
              </div>
            )},
            { title: 'MAC', dataIndex: 'mac_address', key: 'mac_address', width: 160, render: (t) => <Text code style={{ fontSize: 11 }}>{t}</Text> },
            { title: 'Durum', dataIndex: 'status', key: 'status', width: 110, render: (s) => {
              const c = { online: { color: 'green', text: 'Çevrimiçi' }, offline: { color: 'red', text: 'Çevrimdışı' }, maintenance: { color: 'orange', text: 'Bakım' }, error: { color: 'red', text: 'Hata' } };
              const cfg = c[s] || { color: 'default', text: s };
              return <Tag color={cfg.color}>{cfg.text}</Tag>;
            }},
            { title: 'Firmware', dataIndex: 'firmware_version', key: 'firmware_version', width: 110, render: (v) => v ? <Tag>{v}</Tag> : <Tag color="orange">?</Tag> },
            { title: 'Müşteri', dataIndex: ['customer', 'name'], key: 'customer', width: 120, render: (n) => n || '-' },
            { title: 'Son Görülme', dataIndex: 'last_seen', key: 'last_seen', width: 160, render: (d) => d ? safeFormatTimestamp(d) : '-' },
            {
              title: 'İlerleme', key: 'deploy_status', width: 200,
              render: (_, r) => {
                const rtp = otaProgress[r.id];
                if (rtp) {
                  const stageMap = { downloading: 'İndiriliyor', verifying: 'Doğrulanıyor', installing: 'Yükleniyor' };
                  return (
                    <div>
                      <Tag color="processing">{stageMap[rtp.stage] || rtp.stage}</Tag>
                      <Progress percent={rtp.percent} size="small" status="active" />
                    </div>
                  );
                }
                const p = deployProgress[r.id];
                if (p === 0) return <Tag color="blue">Gönderiliyor...</Tag>;
                if (p === 100) return <Tag color="green" icon={<CheckCircleOutlined />}>Gönderildi</Tag>;
                if (p === -1) return <Tag color="red" icon={<CloseCircleOutlined />}>Hata</Tag>;
                return null;
              }
            }
          ]}
        />
      </Modal>

      {/* Deployment Report Modal */}
      <Modal
        title="OTA Dağıtım Raporu"
        open={reportModalVisible}
        onCancel={() => setReportModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setReportModalVisible(false)}>Kapat</Button>,
          <Button key="refresh" type="primary" icon={<ReloadOutlined />} onClick={() => { fetchOTAHistory(); setReportModalVisible(false); }}>OTA Geçmişini Yenile</Button>
        ]}
        width={700}
      >
        {/* Compact summary */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space size="large">
            <span><Text style={{ marginRight: 4 }}>Toplam</Text><Text strong style={{ fontSize: 18 }}>{deploymentReport.length}</Text></span>
            <span><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} /><Text style={{ marginRight: 4 }}>Başarılı</Text><Text strong style={{ color: '#52c41a', fontSize: 18 }}>{deploymentReport.filter(r => r.success).length}</Text></span>
            {deploymentReport.filter(r => !r.success).length > 0 && (
              <span><CloseCircleOutlined style={{ color: '#f5222d', marginRight: 4 }} /><Text style={{ marginRight: 4 }}>Başarısız</Text><Text strong style={{ color: '#f5222d', fontSize: 18 }}>{deploymentReport.filter(r => !r.success).length}</Text></span>
            )}
          </Space>
        </Card>

        <Table
          dataSource={deploymentReport}
          rowKey={(r) => r.device_id}
          pagination={false}
          size="small"
          columns={[
            { title: 'Cihaz', dataIndex: 'device_name', key: 'device_name', width: 180 },
            { title: 'Durum', key: 'status', width: 110, render: (_, r) => r.success ? <Tag color="green" icon={<CheckCircleOutlined />}>Başarılı</Tag> : <Tag color="red" icon={<CloseCircleOutlined />}>Başarısız</Tag> },
            { title: 'Mesaj', key: 'message', ellipsis: true, render: (_, r) => r.success ? <Text style={{ color: '#52c41a' }}>{r.message}</Text> : <Text type="danger">{r.error}</Text> },
            {
              title: '', key: 'action', width: 80,
              render: (_, r) => !r.success && (
                <Button size="small" icon={<ReloadOutlined />} onClick={async () => {
                  try { await axios.post(`/api/v1/firmware/${r.firmware_id}/deploy/${r.device_id}`); message.success('Tekrar gönderildi'); setReportModalVisible(false); } catch (e) { message.error(e.response?.data?.detail || 'Hata'); }
                }}>Tekrar</Button>
              )
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default Firmware;
